import React from 'react';
import {useTranslation} from 'react-i18next';
import {NavLink, useLocation} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import useSWR from 'swr';
import {map, uniq} from 'lodash';
import api, {getURL} from 'api';
import useMixPanel from 'hooks/useMixPanel';
import {setHousingId, setDealName} from '../../store/slices/guestParamsEventsSlice';
import {GUEST_APP_EVENTS_NAME} from 'utils/constants';
import {Offer} from 'utils/upselling/types';
import {OFFER_LABELS, OFFER_PRICE_TYPES, OFFER_CONFIRMATION_TYPES} from '../../utils/upselling';
import {useReservation} from 'context/reservation';
import {useCartDeals} from '../../hooks';
import {useComputedReservationDetails} from '../../context/computedReservationDetails';
import {getNumberOfGuests} from 'utils/reservation';
import {useErrorModal, useIsMounted} from 'utils/hooks';
import {usePaymentSettings} from 'hooks/usePaymentSettings';
import experiencesHeaderUpselling from '../../assets/img/experiences-header-upselling.png';
import backgroundPlaceholder from '../../assets/empty-offer-background.svg';
import transportationImage from '../../assets/img/transportationImage.png';
import Page from 'components/acommon/Page';
import ExperienceCard from 'views/ExperiencesPage/ExperienceCard';
import ExperiencesNavigation from './ExperiencesNavigation';
import PaymentFixedButton from './shared/PaymentFixedButton';
import {
  StyledPage,
  Header,
  Title,
  SubTitle,
  Row,
  Catalog,
  UpsellingMobileNavigation,
  UpsellingDesktopBackButton,
} from './styled';

export const TempWelcomePickupsOfferData = {
  id: 'welcome-pickups',
  image_url: transportationImage,
  confirmation_type: OFFER_CONFIRMATION_TYPES.auto,
  price: '0',
  title: 'Airport transfer (By Welcome Pickups)',
  highlight: '',
  description: 'Description',
};

function fetchOffersIfCanFetch(housingId: string | undefined, categoryName: string) {
  if (!housingId) {
    return null;
  }

  return getURL(
    api.upselling.ENDPOINTS.offers(
      `housing_id=${housingId}&category=${categoryName || ''}&is_active=true`,
    ),
  );
}

function ExperiencesPage() {
  const {t} = useTranslation();
  const location = useLocation();
  const isMounted = useIsMounted();
  const {data: reservation} = useReservation();
  const {formattedCheckInDate, formattedCheckOutDate} = useComputedReservationDetails();
  const {ErrorModal, displayError} = useErrorModal();
  const housingId = reservation?.housing?.id;
  const numberOfGuests = getNumberOfGuests(reservation);
  const {paymentSettingsCurrencyLabel} = usePaymentSettings();
  const {IdGuestAppEvents} = useMixPanel();
  const dispatch = useDispatch();

  const {storedCartDeals} = useCartDeals({
    onError: displayError,
  });

  const [categories, setCategories] = React.useState<string[]>([]);

  const [selectedCategory, setSelectedCategory] = React.useState<string>('');

  const {data: offers} = useSWR<Offer[]>(
    fetchOffersIfCanFetch(housingId, selectedCategory),
    {
      onError: error => {
        if (isMounted.current) {
          displayError(error);
        }
      },
      revalidateIfStale: false,
    },
  );

  React.useEffect(() => {
    if (categories.length < uniq(map(offers, 'category')).length) {
      setCategories(uniq(map(offers, 'category')));
    }
  }, [categories.length, offers]);

  React.useEffect(() => {
    IdGuestAppEvents(GUEST_APP_EVENTS_NAME.upsellings);
  }, [IdGuestAppEvents]);

  React.useEffect(() => {
    if (housingId) {
      dispatch(setHousingId(housingId));
    }
  }, [housingId, dispatch]);

  return (
    <Page showFooterOnlyMobile showHeaderOnlyDesktop paddingBottomMobileFooter>
      <UpsellingMobileNavigation
        title={t('experiences')}
        showHomeButton
        showMenuButton={false}
      />
      <StyledPage>
        <Row className="pageHeaderRow">
          <UpsellingDesktopBackButton />
          <Header>
            <Title>{t('experiences')}</Title>
            <SubTitle>{t('find_and_book_your_adventure')}</SubTitle>
          </Header>
          <img src={experiencesHeaderUpselling} alt="" />
        </Row>
        <Row className="filterRow">
          <div className="leftSideOfRow">
            {/* <Off25>UP TO 25% OFF</Off25> */}
            <div
              style={{
                color: '#23262F',
                fontSize: '16px',
                marginLeft: '30px',
              }}
            >
              {`${formattedCheckInDate} - ${formattedCheckOutDate}. ${numberOfGuests} ${t(
                'guests',
              )}.`}
            </div>
          </div>
          {/* <Select
            options={filterOptions}
            defaultValue={{
              label: 'All experiances',
              value: 'all',
            }}
          /> */}
        </Row>

        <ExperiencesNavigation
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          categories={categories}
        />
        <Row style={{paddingBottom: '50px'}}>
          <Catalog>
            {map(offers, offer => {
              const {id, image_url, title, items, highlight, description, price_type} =
                offer;
              const selectedLabel = storedCartDeals[offer.id] && OFFER_LABELS.selected;
              const label = selectedLabel || '';
              const image = image_url || backgroundPlaceholder;

              const price =
                price_type === OFFER_PRICE_TYPES.single
                  ? items[0].price
                  : items.sort((a, b) => Number(a.price) - Number(b.price))[0].price;

              const priceWithCondition =
                items.length === 1
                  ? `${price} ${paymentSettingsCurrencyLabel} ${t('total_lowercase')}`
                  : `${t('starting_from')} ${price} ${paymentSettingsCurrencyLabel}`;

              return (
                <NavLink
                  key={id}
                  to={{pathname: `/experiences/${id}`, state: location.state}}
                  className="experienceLink"
                  activeClassName="active"
                  onClick={() => {
                    dispatch(setDealName(title));
                    IdGuestAppEvents(GUEST_APP_EVENTS_NAME.dealClicked, {
                      dealName: title,
                    });
                  }}
                >
                  <ExperienceCard
                    img={image}
                    name={title}
                    label={label}
                    hours={t('no_data')}
                    people={t('no_data')}
                    price={priceWithCondition}
                    highlight={highlight}
                    desription={description}
                    spots={t('no_data')}
                  />
                </NavLink>
              );
            })}
            <NavLink
              key={Math.random()}
              to={{pathname: `/experiences/welcome-pickups`, state: location.state}}
              className="experienceLink"
              activeClassName="active"
            >
              <ExperienceCard
                img={TempWelcomePickupsOfferData.image_url}
                name={TempWelcomePickupsOfferData.title}
                hours={t('no_data')}
                people={t('no_data')}
                price={
                  TempWelcomePickupsOfferData.price + ' ' + paymentSettingsCurrencyLabel
                }
                highlight={TempWelcomePickupsOfferData.highlight}
                desription={TempWelcomePickupsOfferData.description}
                spots={t('no_data')}
              />
            </NavLink>
          </Catalog>
        </Row>
        <PaymentFixedButton onError={displayError} />
      </StyledPage>
      <ErrorModal />
    </Page>
  );
}

export default ExperiencesPage;
